import { generateUUID } from 'services/base/uuid';
import { UnreachableFunction } from 'ui/base/types';

import {
  ProtocolFilterType,
  ApplicationProtocolFilter,
  ApplicationProtocolFilterDetails,
  APPLICATION_PROTOCOL_FILTERS,
  PortTransportProtocolFilter,
  RangeTransportProtocolFilter,
  ProtocolFilter,
} from 'services/pangolin/types';

import {
  TransportProtocol,
  ApplicationProtocol,
  getShortTransportProtocol,
  getFullApplicationProtocol,
} from 'assets/enums/protocols/protocols';

import Messages from './protocol_entry_messages';

export type ProtocolIconStyles = {
  customIcon: string;
  hubIcon: string;
  lanIcon: string;
  mailIcon: string;
  shellIcon: string;
  storageIcon: string;
  timeIcon: string;
  webIcon: string;
};

const APPLICATION_PROTOCOL_ICONS = new Map<ApplicationProtocol, (s: ProtocolIconStyles) => string>([
  [ApplicationProtocol.FTP, s => s.storageIcon],
  [ApplicationProtocol.SSH, s => s.shellIcon],
  [ApplicationProtocol.TELNET, s => s.shellIcon],
  [ApplicationProtocol.SMTP, s => s.mailIcon],
  [ApplicationProtocol.DNS, s => s.hubIcon],
  [ApplicationProtocol.DHCP, s => s.lanIcon],
  [ApplicationProtocol.TFTP, s => s.storageIcon],
  [ApplicationProtocol.HTTP, s => s.webIcon],
  [ApplicationProtocol.POP2, s => s.mailIcon],
  [ApplicationProtocol.POP3, s => s.mailIcon],
  [ApplicationProtocol.NTP, s => s.timeIcon],
  [ApplicationProtocol.NETBIOS, s => s.lanIcon],
  [ApplicationProtocol.IMAP, s => s.mailIcon],
  [ApplicationProtocol.HTTPS, s => s.webIcon],
]);

export const getProtocolFilterIcon = (styles: ProtocolIconStyles, entry: ProtocolFilter): string | undefined => {
  switch(entry.type) {
    case ProtocolFilterType.TRANSPORT_PORT:
    case ProtocolFilterType.TRANSPORT_RANGE: {
      return styles.customIcon;
    }
    case ProtocolFilterType.APPLICATION: {
      const icon = APPLICATION_PROTOCOL_ICONS.get(entry.application);
      return icon !== undefined ? icon(styles) : undefined;
    }
    default: {
      return UnreachableFunction(entry);
    }
  }
};

export const getProtocolFilterLabel = (entry: ProtocolFilter, option?: boolean): string => {
  switch(entry.type) {
    case ProtocolFilterType.TRANSPORT_PORT: {
      if (option) return Messages.portOption();
      const p_name = entry.transport !== undefined ?
                     getShortTransportProtocol(entry.transport) :
                     Messages.anyProtocol();
      return `${p_name}: ${entry.port}`;
    }
    case ProtocolFilterType.TRANSPORT_RANGE: {
      if (option) return Messages.rangeOption();
      const r_name = entry.transport !== undefined ?
                     getShortTransportProtocol(entry.transport) :
                     Messages.anyProtocol();
      return `${r_name}: ${entry.port_first} — ${entry.port_final}`;
    }
    case ProtocolFilterType.APPLICATION: {
      const a_details = APPLICATION_PROTOCOL_FILTERS.get(entry.application);
      return a_details !== undefined ?
             getFullApplicationProtocol(entry.application) :
             Messages.anyProtocol();
    }
    default: {
      return UnreachableFunction(entry);
    }
  }
};

export const getProtocolFilterID = (entry: ProtocolFilter): string => {
  return entry.uuid;
};

export function isKnownProtocolFilter(x: ProtocolFilter): x is ApplicationProtocolFilter {
  return x.type !== ProtocolFilterType.APPLICATION;
}

export function createProtocolFilterOptions(existingEntries: ProtocolFilter[]): ProtocolFilter[] {
  const result: ProtocolFilter[] = [{
    type: ProtocolFilterType.TRANSPORT_PORT,
    uuid: generateUUID(),
    transport: undefined,
    port: 0,
  }, {
    type: ProtocolFilterType.TRANSPORT_RANGE,
    uuid: generateUUID(),
    transport: undefined,
    port_first: 0,
    port_final: 0,
  }];

  APPLICATION_PROTOCOL_FILTERS.forEach((details: ApplicationProtocolFilterDetails,
                                        application: ApplicationProtocol) => {
    const existing = existingEntries.find(
      entry =>
        entry.type === ProtocolFilterType.APPLICATION &&
        entry.application === application
    );

    result.push(
      existing != null ? existing :
      details.filters.application
    );
  });

  return result;
}

export function createPortFilter(transport: TransportProtocol|undefined,
                                 port: number): PortTransportProtocolFilter {
  return {
    type: ProtocolFilterType.TRANSPORT_PORT,
    uuid: generateUUID(), transport, port,
  };
}

export function createRangeFilter(transport: TransportProtocol|undefined,
                                  port_first: number,
                                  port_final: number): RangeTransportProtocolFilter {
  return {
    type: ProtocolFilterType.TRANSPORT_RANGE,
    uuid: generateUUID(), transport,
    port_first: Math.min(port_first, port_final),
    port_final: Math.max(port_first, port_final),
  };
}
